import { Game, RankifierConfiguration } from "../gql/graphql";
import { DestinationKind, RankifierCardType } from "../contexts/rankifier";

export type RankifierCardsData = {
  [key in DestinationKind]: (RankifierCardType | null)[];
};

export interface RankifierGameConfiguration extends RankifierConfiguration {
  numberOfHands: number;
  cardsPerHand: number;
}

export interface RankifierGame extends Omit<Game, "configuration"> {
  configuration: RankifierGameConfiguration;
}

export interface RankifierState {
  game: RankifierGame;
  cards: RankifierCardsData;
  results: {
    [key in string]: {
      minuses: number;
      plusses: number;
      zeroes: number;
    };
  };
  status: {
    round: number;
    shouldChangeRound: boolean;
    isGameFinished: boolean;
    isPickingFavorite: boolean;
    startTime: Date;
  };
}

export type RankifierAction = { type: "NEXT_ROUND" } | { type: "SELECT_CARD"; payload: { card: RankifierCardType } };

export type RankifierReducerType = (state: RankifierState, action: RankifierAction) => RankifierState;

export const rankifierReducer: RankifierReducerType = (state, action) => {
  switch (action.type) {
    case "NEXT_ROUND": {
      const {
        results,
        status: { shouldChangeRound, isGameFinished, round },
        cards: { all, deck, hand },
        game: {
          configuration: { cardsPerHand, numberOfHands },
        },
      } = state;

      if (isGameFinished || !shouldChangeRound) {
        return state;
      }

      const newRound = round + 1;
      const zeroes = hand.filter((card) => card !== null);
      const newResults = { ...results };
      zeroes.forEach((card) => {
        if (!card) {
          return;
        }
        const result = { ...newResults[card.attribute.id as string] };
        result.zeroes += 1;
        newResults[card.attribute.id as string] = result;
      });
      const newAll = all.filter((card) => !zeroes.includes(card));
      const newHand = deck.slice(round * cardsPerHand, round * cardsPerHand + cardsPerHand);
      const newDeck = [
        ...deck.slice(0, round * cardsPerHand),
        ...new Array(cardsPerHand).fill(null),
        ...deck.slice(round * cardsPerHand + cardsPerHand),
      ];

      return {
        ...state,
        cards: {
          ...state.cards,
          all: newAll,
          deck: newDeck,
          hand: newHand,
        },
        status: {
          ...state.status,
          round: newRound,
          shouldChangeRound: false,
          isGameFinished: newRound > numberOfHands,
        },
        results: newResults,
      };
    }
    case "SELECT_CARD": {
      const {
        results,
        status: { isPickingFavorite, isGameFinished, shouldChangeRound },
        cards: { hand, selected, all },
      } = state;
      const { card } = action.payload;
      const result = { ...results[card.attribute.id as string] };

      if (isGameFinished || shouldChangeRound) {
        return state;
      }

      const cardHandIndex = hand.indexOf(card);

      if (cardHandIndex === -1) {
        return state;
      }

      const newHand = [...hand];
      const newSelected = [...selected];
      let newAll = [...all];
      let newShouldChangeRound = false;
      newHand[cardHandIndex] = null;

      if (isPickingFavorite) {
        const nullIndex = selected.indexOf(null);
        newSelected[nullIndex] = card;
      } else {
        const cardAllIndex = all.indexOf(card);
        newAll = [...all.slice(0, cardAllIndex), ...all.slice(cardAllIndex + 1)];
        newShouldChangeRound = true;
      }

      result[isPickingFavorite ? "plusses" : "minuses"] += 1;

      return {
        ...state,
        cards: {
          ...state.cards,
          all: newAll,
          hand: newHand,
          selected: newSelected,
        },
        status: {
          ...state.status,
          shouldChangeRound: newShouldChangeRound,
          isPickingFavorite: !isPickingFavorite,
        },
        results: {
          ...results,
          [card.attribute.id as string]: result,
        },
      };
    }
    default:
      return state;
  }
};
